import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import ERPLayout from '@/components/feature/ERPLayout';
import { quotationsApi } from '@/services/api';
import { mockQuotations, type Quotation, type QuotationStatus } from '@/mocks/quotations';
import { exportToCSV, exportToExcel, exportToPDF } from '@/utils/export';

// ── Normalize DB row → Quotation shape ───────────────────────────────────────
// Backend returns snake_case DB columns; the page uses the camelCase Quotation type.
function normalizeQuotation(row: Record<string, unknown>): Quotation {
  // Already normalized (e.g. from mock data)
  if (typeof row.quotationNo === 'string') return row as unknown as Quotation;

  const statusMap: Record<string, QuotationStatus> = {
    draft: 'draft', sent: 'sent', pending_approval: 'pending_approval',
    approved: 'approved', rejected: 'rejected', expired: 'expired', converted: 'converted',
  };
  const rawStatus = String(row.status || 'draft').toLowerCase();
  const status: QuotationStatus = statusMap[rawStatus] || 'draft';

  const subtotal = Number(row.subtotal || 0);
  const totalTax = Number(row.tax_amount || 0);
  const grandTotal = Number(row.total || subtotal + totalTax);

  // Items may be embedded or absent in list view
  const items = Array.isArray(row.items)
    ? (row.items as Record<string, unknown>[]).map((item, idx) => ({
        id: Number(item.id || idx),
        description: String(item.description || ''),
        qty: Number(item.qty || 0),
        unit: String(item.unit || ''),
        rate: Number(item.rate || 0),
        taxPct: Number(item.tax_pct || 18),
      }))
    : [];

  return {
    id: Number(row.id),
    quotationNo: String(row.doc_no || row.quotationNo || ''),
    date: row.created_at ? String(row.created_at).split('T')[0] : String(row.date || ''),
    validUntil: row.due_date ? String(row.due_date).split('T')[0] : String(row.validUntil || ''),
    status,
    clientId: Number(row.client_id || 0),
    clientName: String(row.client_name || row.clientName || ''),
    clientCompany: String(row.client_company || row.clientCompany || ''),
    clientEmail: String(row.client_email || row.clientEmail || ''),
    clientPhone: String(row.client_phone || row.clientPhone || ''),
    clientAddress: String(row.client_address || row.clientAddress || ''),
    clientGstin: String(row.client_gstin || row.clientGstin || ''),
    jobRef: String(row.job_no || row.jobRef || ''),
    jobTitle: String(row.job_title || row.jobTitle || ''),
    jobLocation: String(row.job_location || row.jobLocation || ''),
    items,
    notes: String(row.notes || ''),
    terms: String(row.terms || ''),
    subtotal,
    totalTax,
    grandTotal,
    convertedJobNo: row.converted_job_no ? String(row.converted_job_no) : (row.convertedJobNo ? String(row.convertedJobNo) : null),
    createdBy: String(row.created_by_name || row.createdBy || ''),
    approvedBy: row.approved_by_name ? String(row.approved_by_name) : (row.approvedBy ? String(row.approvedBy) : null),
    approvedAt: row.approved_at ? String(row.approved_at).split('T')[0] : (row.approvedAt ? String(row.approvedAt) : null),
    sentAt: row.sent_at ? String(row.sent_at).split('T')[0] : (row.sentAt ? String(row.sentAt) : null),
    createdAt: String(row.created_at || row.createdAt || ''),
    updatedAt: String(row.updated_at || row.updatedAt || ''),
  };
}

// ── Convert to Invoice Modal ────────────────────────────────────────────────
interface ConvertInvoiceModalProps {
  quotation: Quotation;
  onConfirm: (invoiceNo: string, dueDate: string) => Promise<void>;
  onCancel: () => void;
  converting: boolean;
}

function ConvertInvoiceModal({ quotation, onConfirm, onCancel, converting }: ConvertInvoiceModalProps) {
  const today = new Date().toISOString().split('T')[0];
  const defaultDue = new Date(Date.now() + 30 * 86400000).toISOString().split('T')[0];
  const year = new Date().getFullYear();
  const seq = String(Math.floor(Math.random() * 9000) + 1000);
  const [invoiceNo, setInvoiceNo] = useState(`INV-${year}-${seq}`);
  const [dueDate, setDueDate] = useState(defaultDue);

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-100 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center flex-shrink-0">
            <i className="ri-file-text-line text-xl text-amber-600" />
          </div>
          <div>
            <h3 className="font-bold text-gray-900">Convert to Invoice</h3>
            <p className="text-xs text-gray-500 mt-0.5">{quotation.quotationNo} · {quotation.clientCompany}</p>
          </div>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div className="bg-amber-50 rounded-xl p-4 space-y-1.5">
            <div className="flex justify-between text-xs">
              <span className="text-gray-500">Client</span>
              <span className="font-semibold text-gray-800">{quotation.clientName}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-gray-500">Job / Scope</span>
              <span className="font-semibold text-gray-800 text-right max-w-[200px] truncate">{quotation.jobTitle}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-gray-500">Subtotal</span>
              <span className="font-semibold text-gray-800">₹{quotation.subtotal.toLocaleString('en-IN')}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-gray-500">GST ({Math.round((quotation.totalTax / quotation.subtotal) * 100)}%)</span>
              <span className="font-semibold text-gray-800">₹{quotation.totalTax.toLocaleString('en-IN')}</span>
            </div>
            <div className="flex justify-between text-sm border-t border-amber-200 pt-1.5 mt-1.5">
              <span className="font-bold text-gray-700">Grand Total</span>
              <span className="font-bold text-amber-700">₹{quotation.grandTotal.toLocaleString('en-IN')}</span>
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1.5 block">Invoice Number</label>
            <input
              type="text"
              value={invoiceNo}
              onChange={(e) => setInvoiceNo(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1.5 block">Invoice Date</label>
              <input
                type="date"
                defaultValue={today}
                readOnly
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-gray-50 text-gray-500 cursor-not-allowed"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1.5 block">Due Date</label>
              <input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400"
              />
            </div>
          </div>
          <p className="text-xs text-gray-400">
            All line items, GST, and client details will be copied from the quotation. The quotation status will be updated to <strong>Converted</strong>.
          </p>
        </div>
        <div className="px-6 py-4 border-t border-gray-100 flex gap-3">
          <button onClick={onCancel} disabled={converting}
            className="flex-1 py-2.5 border border-gray-200 text-gray-600 rounded-xl text-sm font-semibold cursor-pointer hover:bg-gray-50 transition-all whitespace-nowrap disabled:opacity-50">
            Cancel
          </button>
          <button onClick={() => onConfirm(invoiceNo, dueDate)} disabled={converting || !invoiceNo.trim()}
            className="flex-1 py-2.5 bg-amber-600 text-white rounded-xl text-sm font-bold cursor-pointer hover:bg-amber-700 transition-all whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2">
            {converting ? (
              <><i className="ri-loader-4-line animate-spin text-sm" />Creating...</>
            ) : (
              <><i className="ri-file-text-line text-sm" />Create Invoice</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

const STATUS_META: Record<QuotationStatus, { label: string; bg: string; text: string; icon: string }> = {
  draft:            { label: 'Draft',            bg: '#f3f4f6', text: '#6b7280', icon: 'ri-draft-line' },
  sent:             { label: 'Sent',             bg: '#fef3c7', text: '#b45309', icon: 'ri-send-plane-line' },
  pending_approval: { label: 'Pending Approval', bg: '#fffbeb', text: '#d97706', icon: 'ri-time-line' },
  approved:         { label: 'Approved',         bg: '#f0fdf4', text: '#16a34a', icon: 'ri-checkbox-circle-line' },
  rejected:         { label: 'Rejected',         bg: '#fef2f2', text: '#dc2626', icon: 'ri-close-circle-line' },
  expired:          { label: 'Expired',          bg: '#fff7ed', text: '#ea580c', icon: 'ri-alarm-warning-line' },
  converted:        { label: 'Converted',        bg: '#ecfdf5', text: '#059669', icon: 'ri-arrow-right-circle-line' },
};

type SortKey = 'quotationNo' | 'date' | 'grandTotal' | 'clientName' | 'status';

interface DeleteModalProps {
  quotation: Quotation;
  onConfirm: () => void;
  onCancel: () => void;
}

function DeleteModal({ quotation, onConfirm, onCancel }: DeleteModalProps) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-sm overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-100 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center flex-shrink-0">
            <i className="ri-delete-bin-line text-xl text-red-500" />
          </div>
          <div>
            <h3 className="font-bold text-gray-900">Delete Quotation</h3>
            <p className="text-xs text-gray-500 mt-0.5">{quotation.quotationNo}</p>
          </div>
        </div>
        <div className="px-6 py-5">
          <p className="text-sm text-gray-600 leading-relaxed">
            Are you sure you want to delete <strong>{quotation.quotationNo}</strong> for{' '}
            <strong>{quotation.clientCompany}</strong>? This action cannot be undone.
          </p>
        </div>
        <div className="px-6 py-4 border-t border-gray-100 flex gap-3">
          <button onClick={onCancel} className="flex-1 py-2.5 border border-gray-200 text-gray-600 rounded-xl text-sm font-semibold cursor-pointer hover:bg-gray-50 transition-all whitespace-nowrap">
            Cancel
          </button>
          <button onClick={onConfirm} className="flex-1 py-2.5 bg-red-500 text-white rounded-xl text-sm font-bold cursor-pointer hover:bg-red-600 transition-all whitespace-nowrap">
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

interface StatusChangeModalProps {
  quotation: Quotation;
  newStatus: QuotationStatus;
  onConfirm: () => void;
  onCancel: () => void;
}

function StatusChangeModal({ quotation, newStatus, onConfirm, onCancel }: StatusChangeModalProps) {
  const meta = STATUS_META[newStatus];
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-sm overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-100 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: meta.bg }}>
            <i className={`${meta.icon} text-xl`} style={{ color: meta.text }} />
          </div>
          <div>
            <h3 className="font-bold text-gray-900">Change Status to {meta.label}</h3>
            <p className="text-xs text-gray-500 mt-0.5">{quotation.quotationNo}</p>
          </div>
        </div>
        <div className="px-6 py-5">
          <p className="text-sm text-gray-600 leading-relaxed">
            Update <strong>{quotation.quotationNo}</strong> status to{' '}
            <span className="font-bold" style={{ color: meta.text }}>{meta.label}</span>?
          </p>
        </div>
        <div className="px-6 py-4 border-t border-gray-100 flex gap-3">
          <button onClick={onCancel} className="flex-1 py-2.5 border border-gray-200 text-gray-600 rounded-xl text-sm font-semibold cursor-pointer hover:bg-gray-50 transition-all whitespace-nowrap">
            Cancel
          </button>
          <button onClick={onConfirm} className="flex-1 py-2.5 text-white rounded-xl text-sm font-bold cursor-pointer transition-all whitespace-nowrap" style={{ background: meta.text }}>
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
}

export default function QuotationsPage() {
  const navigate = useNavigate();
  const [quotations, setQuotations] = useState<Quotation[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<QuotationStatus | 'all'>('all');
  const [sortKey, setSortKey] = useState<SortKey>('date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [deleteTarget, setDeleteTarget] = useState<Quotation | null>(null);
  const [statusTarget, setStatusTarget] = useState<{ q: Quotation; s: QuotationStatus } | null>(null);
  const [convertTarget, setConvertTarget] = useState<Quotation | null>(null);
  const [converting, setConverting] = useState(false);
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  const [toast, setToast] = useState('');
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const exportMenuRef = useRef<HTMLDivElement>(null);

  // Close export menu on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target as Node)) {
        setExportMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleExportCSV = () => {
    const data = sorted.map((q) => ({
      'Quotation No': q.quotationNo,
      'Date': q.date,
      'Valid Until': q.validUntil,
      'Status': q.status,
      'Client Name': q.clientName,
      'Client Company': q.clientCompany,
      'Job Title': q.jobTitle,
      'Subtotal': q.subtotal,
      'Total Tax': q.totalTax,
      'Grand Total': q.grandTotal,
      'Created By': q.createdBy,
    }));
    exportToCSV(data, `quotations-${new Date().toISOString().split('T')[0]}`);
    setExportMenuOpen(false);
  };

  const handleExportExcel = async () => {
    const data = sorted.map((q) => ({
      'Quotation No': q.quotationNo,
      'Date': q.date,
      'Valid Until': q.validUntil,
      'Status': q.status,
      'Client Name': q.clientName,
      'Client Company': q.clientCompany,
      'Job Title': q.jobTitle,
      'Subtotal': q.subtotal,
      'Total Tax': q.totalTax,
      'Grand Total': q.grandTotal,
      'Created By': q.createdBy,
    }));
    await exportToExcel(data, `quotations-${new Date().toISOString().split('T')[0]}`, 'Quotations');
    setExportMenuOpen(false);
  };

  const handleExportPDF = () => {
    exportToPDF('quotations-table', `quotations-${new Date().toISOString().split('T')[0]}`, 'Quotations Report');
    setExportMenuOpen(false);
  };

  const fetchQuotations = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (search.trim()) params.search = search.trim();
      const data = await quotationsApi.getAll(params);
      const rows: Record<string, unknown>[] = Array.isArray(data) ? data : data.data ?? [];
      setQuotations(rows.map(normalizeQuotation));
    } catch {
      let fallback = mockQuotations as Quotation[];
      if (statusFilter !== 'all') fallback = fallback.filter((q) => q.status === statusFilter);
      if (search.trim()) {
        const q = search.toLowerCase();
        fallback = fallback.filter(
          (item) =>
            item.quotationNo.toLowerCase().includes(q) ||
            item.clientName.toLowerCase().includes(q) ||
            item.clientCompany.toLowerCase().includes(q) ||
            item.jobTitle.toLowerCase().includes(q)
        );
      }
      setQuotations(fallback);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, search]);

  useEffect(() => {
    const timer = setTimeout(fetchQuotations, 300);
    return () => clearTimeout(timer);
  }, [fetchQuotations]);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('desc'); }
  };

  const sorted = [...quotations].sort((a, b) => {
    let av: string | number = a[sortKey] as string | number;
    let bv: string | number = b[sortKey] as string | number;
    if (typeof av === 'string') av = av.toLowerCase();
    if (typeof bv === 'string') bv = bv.toLowerCase();
    if (av < bv) return sortDir === 'asc' ? -1 : 1;
    if (av > bv) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const stats = {
    total: quotations.length,
    pending: quotations.filter((q) => q.status === 'pending_approval').length,
    approvedValue: quotations.filter((q) => q.status === 'approved' || q.status === 'converted').reduce((a, b) => a + b.grandTotal, 0),
    totalValue: quotations.reduce((a, b) => a + b.grandTotal, 0),
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await quotationsApi.delete(deleteTarget.id);
    } catch {
      // Optimistic
    }
    setQuotations((prev) => prev.filter((q) => q.id !== deleteTarget.id));
    showToast(`${deleteTarget.quotationNo} deleted`);
    setDeleteTarget(null);
  };

  const confirmStatusChange = async () => {
    if (!statusTarget) return;
    try {
      await quotationsApi.updateStatus(statusTarget.q.id, statusTarget.s);
    } catch {
      // Optimistic
    }
    setQuotations((prev) =>
      prev.map((q) => q.id === statusTarget.q.id ? { ...q, status: statusTarget.s } : q)
    );
    showToast(`${statusTarget.q.quotationNo} marked as ${STATUS_META[statusTarget.s].label}`);
    setStatusTarget(null);
  };

  const handleConvertToInvoice = async (invoiceNo: string, dueDate: string) => {
    if (!convertTarget) return;
    setConverting(true);
    try {
      await quotationsApi.convertToInvoice(convertTarget.id);
    } catch {
      // Optimistic — API may not be live yet
    }
    // Mark quotation as converted
    setQuotations((prev) =>
      prev.map((q) =>
        q.id === convertTarget.id
          ? { ...q, status: 'converted' as QuotationStatus, convertedJobNo: invoiceNo }
          : q
      )
    );
    showToast(`Invoice ${invoiceNo} created from ${convertTarget.quotationNo}`);
    setConvertTarget(null);
    setConverting(false);
  };

  const handleDuplicate = async (q: Quotation) => {
    const now = new Date();
    const newNo = `QT-${now.getFullYear()}-${String(Math.floor(Math.random() * 900) + 100)}`;
    const dup: Quotation = {
      ...q,
      id: Date.now(),
      quotationNo: newNo,
      status: 'draft',
      date: now.toISOString().split('T')[0],
      validUntil: new Date(now.getTime() + 30 * 86400000).toISOString().split('T')[0],
      convertedJobNo: null,
      approvedBy: null,
      approvedAt: null,
      sentAt: null,
      createdAt: now.toISOString(),
      updatedAt: now.toISOString(),
    };
    try {
      await quotationsApi.create(dup as unknown as Record<string, unknown>);
    } catch {
      // Optimistic
    }
    setQuotations((prev) => [dup, ...prev]);
    showToast(`Duplicated as ${newNo}`);
    setOpenMenuId(null);
  };

  const SortIcon = ({ k }: { k: SortKey }) => (
    <span className="ml-1 inline-flex flex-col gap-0.5">
      <i className={`ri-arrow-up-s-line text-xs leading-none ${sortKey === k && sortDir === 'asc' ? 'text-amber-600' : 'text-gray-300'}`} />
      <i className={`ri-arrow-down-s-line text-xs leading-none ${sortKey === k && sortDir === 'desc' ? 'text-amber-600' : 'text-gray-300'}`} />
    </span>
  );

  return (
    <ERPLayout title="Quotations">
      {toast && (
        <div className="fixed top-4 right-4 z-50 flex items-center gap-2 bg-gray-900 text-white text-sm px-4 py-2.5 rounded-xl font-medium">
          <i className="ri-checkbox-circle-line text-amber-400" />{toast}
        </div>
      )}

      {deleteTarget && (
        <DeleteModal quotation={deleteTarget} onConfirm={confirmDelete} onCancel={() => setDeleteTarget(null)} />
      )}
      {statusTarget && (
        <StatusChangeModal
          quotation={statusTarget.q}
          newStatus={statusTarget.s}
          onConfirm={confirmStatusChange}
          onCancel={() => setStatusTarget(null)}
        />
      )}
      {convertTarget && (
        <ConvertInvoiceModal
          quotation={convertTarget}
          onConfirm={handleConvertToInvoice}
          onCancel={() => setConvertTarget(null)}
          converting={converting}
        />
      )}

      <div className="p-6" onClick={() => setOpenMenuId(null)}>
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <button onClick={() => navigate('/billing')} className="text-gray-400 hover:text-gray-600 cursor-pointer transition-colors">
                <i className="ri-arrow-left-line text-sm" />
              </button>
              <h2 className="text-xl font-bold text-gray-900">Quotations</h2>
            </div>
            <p className="text-sm text-gray-500">
              {loading ? 'Loading...' : `${quotations.length} total — manage, approve, and convert quotations`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative" ref={exportMenuRef}>
              <button 
                onClick={() => setExportMenuOpen(!exportMenuOpen)}
                className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap transition-all"
              >
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-download-2-line text-sm" /></span>
                Export
                <i className={`ri-arrow-down-s-line text-xs transition-transform ${exportMenuOpen ? 'rotate-180' : ''}`} />
              </button>
              {exportMenuOpen && (
                <div className="absolute right-0 top-full mt-1 w-40 bg-white border border-gray-200 rounded-xl shadow-lg z-20 overflow-hidden">
                  <button onClick={handleExportCSV} className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer transition-colors text-left">
                    <i className="ri-file-list-3-line text-gray-400" />CSV
                  </button>
                  <button onClick={handleExportExcel} className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer transition-colors text-left">
                    <i className="ri-file-excel-line text-amber-500" />Excel
                  </button>
                  <button onClick={handleExportPDF} className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer transition-colors text-left">
                    <i className="ri-file-pdf-line text-red-500" />PDF
                  </button>
                </div>
              )}
            </div>
            <button onClick={() => navigate('/billing/quotation/new')}
              className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-semibold hover:bg-amber-600 cursor-pointer whitespace-nowrap transition-all">
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line text-sm" /></span>New Quotation
            </button>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {[
            { label: 'Total Quotations', value: stats.total,                                                    sub: 'All time',             icon: 'ri-file-list-3-line',       color: '#6b7280', bg: '#f3f4f6' },
            { label: 'Pending Approval', value: stats.pending,                                                  sub: 'Awaiting client',      icon: 'ri-time-line',              color: '#7c3aed', bg: '#f5f3ff' },
            { label: 'Approved Value',   value: `₹${(stats.approvedValue / 100000).toFixed(1)}L`,              sub: 'Approved + Converted', icon: 'ri-checkbox-circle-line',   color: '#16a34a', bg: '#f0fdf4' },
            { label: 'Total Pipeline',   value: `₹${(stats.totalValue / 100000).toFixed(1)}L`,                 sub: 'All quotations',       icon: 'ri-money-rupee-circle-line', color: '#059669', bg: '#ecfdf5' },
          ].map((s) => (
            <div key={s.label} className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-4">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: s.bg }}>
                <i className={`${s.icon} text-lg`} style={{ color: s.color }} />
              </div>
              <div>
                <p className="text-xl font-bold text-gray-900">{s.value}</p>
                <p className="text-xs text-gray-500">{s.label}</p>
                <p className="text-xs text-gray-400">{s.sub}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Pending Approval Banner */}
        {stats.pending > 0 && (
          <div className="mb-4 flex items-center gap-3 bg-violet-50 border border-violet-200 rounded-xl px-5 py-3">
            <span className="w-8 h-8 flex items-center justify-center bg-violet-100 rounded-lg flex-shrink-0">
              <i className="ri-time-line text-violet-600" />
            </span>
            <div className="flex-1">
              <p className="text-sm font-semibold text-violet-800">
                {stats.pending} quotation{stats.pending > 1 ? 's' : ''} awaiting client approval
              </p>
              <p className="text-xs text-violet-600 mt-0.5">Follow up with clients or update status after receiving confirmation</p>
            </div>
            <button onClick={() => setStatusFilter('pending_approval')}
              className="text-xs font-semibold text-violet-700 bg-violet-100 hover:bg-violet-200 px-3 py-1.5 rounded-lg cursor-pointer transition-all whitespace-nowrap">
              Filter Pending
            </button>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-xl border border-gray-100 p-4 mb-4 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center pointer-events-none">
              <i className="ri-search-line text-gray-400 text-sm" />
            </span>
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by quotation no., client, or job title..."
              className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 transition-all" />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {(['all', 'draft', 'sent', 'pending_approval', 'approved', 'rejected', 'expired', 'converted'] as const).map((s) => {
              const meta = s === 'all' ? null : STATUS_META[s];
              const isActive = statusFilter === s;
              return (
                <button key={s} onClick={() => setStatusFilter(s)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${isActive ? 'bg-amber-500 text-white' : 'border border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                  {s === 'all' ? 'All' : meta!.label}
                  {s === 'pending_approval' && stats.pending > 0 && (
                    <span className={`ml-1.5 text-xs px-1.5 py-0.5 rounded-full font-bold ${isActive ? 'bg-white/30 text-white' : 'bg-violet-100 text-violet-700'}`}>
                      {stats.pending}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Table */}
        <div id="quotations-table" className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <i className="ri-loader-4-line text-3xl text-amber-500 animate-spin" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50/60">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase cursor-pointer select-none whitespace-nowrap" onClick={() => handleSort('quotationNo')}>
                      Quotation No. <SortIcon k="quotationNo" />
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase cursor-pointer select-none whitespace-nowrap" onClick={() => handleSort('clientName')}>
                      Client <SortIcon k="clientName" />
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase hidden lg:table-cell">Job / Scope</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase cursor-pointer select-none whitespace-nowrap" onClick={() => handleSort('date')}>
                      Date <SortIcon k="date" />
                    </th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase hidden md:table-cell">Valid Until</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase cursor-pointer select-none whitespace-nowrap" onClick={() => handleSort('grandTotal')}>
                      Amount <SortIcon k="grandTotal" />
                    </th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase cursor-pointer select-none whitespace-nowrap" onClick={() => handleSort('status')}>
                      Status <SortIcon k="status" />
                    </th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.length === 0 && (
                    <tr>
                      <td colSpan={8} className="text-center py-16 text-gray-400">
                        <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 bg-gray-100 rounded-xl">
                          <i className="ri-file-list-3-line text-2xl text-gray-300" />
                        </div>
                        <p className="text-sm font-medium">No quotations found</p>
                        <p className="text-xs mt-1">Try adjusting your search or filters</p>
                      </td>
                    </tr>
                  )}
                  {sorted.map((q) => {
                    const meta = STATUS_META[q.status];
                    const isExpiringSoon = q.status === 'sent' && new Date(q.validUntil) <= new Date(Date.now() + 5 * 86400000);
                    return (
                      <tr key={q.id} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors group">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-bold text-gray-800 font-mono">{q.quotationNo}</span>
                            {isExpiringSoon && (
                              <span className="text-xs bg-orange-100 text-orange-600 px-1.5 py-0.5 rounded font-semibold">Expiring</span>
                            )}
                          </div>
                          <p className="text-xs text-gray-400 mt-0.5">by {q.createdBy}</p>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-sm font-semibold text-gray-800">{q.clientName}</p>
                          <p className="text-xs text-gray-500">{q.clientCompany}</p>
                        </td>
                        <td className="px-4 py-3 hidden lg:table-cell">
                          <p className="text-sm text-gray-700 max-w-xs truncate">{q.jobTitle}</p>
                          {q.jobRef && <p className="text-xs font-mono text-gray-400 mt-0.5">{q.jobRef}</p>}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="text-xs text-gray-600">{q.date}</span>
                        </td>
                        <td className="px-4 py-3 text-center hidden md:table-cell">
                          <span className={`text-xs ${isExpiringSoon ? 'text-orange-600 font-semibold' : 'text-gray-600'}`}>{q.validUntil}</span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <p className="text-sm font-bold text-gray-900">₹{q.grandTotal.toLocaleString('en-IN')}</p>
                          <p className="text-xs text-gray-400">+GST ₹{q.totalTax.toLocaleString('en-IN')}</p>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap" style={{ background: meta.bg, color: meta.text }}>
                            <i className={`${meta.icon} mr-1`} />{meta.label}
                          </span>
                          {q.convertedJobNo && (
                            <p className="text-xs text-amber-600 font-mono mt-0.5">{q.convertedJobNo}</p>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center gap-1.5">
                            {q.status === 'pending_approval' && (
                              <button onClick={(e) => { e.stopPropagation(); setStatusTarget({ q, s: 'approved' }); }}
                                className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-bold text-white bg-amber-500 hover:bg-amber-600 cursor-pointer whitespace-nowrap transition-all">
                                <i className="ri-checkbox-circle-line text-xs" />Approve
                              </button>
                            )}
                            {q.status === 'approved' && !q.convertedJobNo && (
                              <button onClick={(e) => { e.stopPropagation(); setConvertTarget(q); }}
                                className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-bold text-white bg-teal-500 hover:bg-teal-600 cursor-pointer whitespace-nowrap transition-all">
                                <i className="ri-file-text-line text-xs" />Invoice
                              </button>
                            )}
                            <button onClick={(e) => { e.stopPropagation(); navigate(`/billing/quotation/${q.quotationNo}/print`); }}
                              className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-amber-600 hover:bg-amber-50 cursor-pointer transition-all"
                              title="Preview">
                              <i className="ri-eye-line text-sm" />
                            </button>
                            <div className="relative" onClick={(e) => e.stopPropagation()}>
                              <button onClick={() => setOpenMenuId(openMenuId === q.id ? null : q.id)}
                                className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 cursor-pointer transition-all">
                                <i className="ri-more-2-line text-sm" />
                              </button>
                              {openMenuId === q.id && (
                                <div ref={menuRef} className="absolute right-0 top-full mt-1 w-52 bg-white border border-gray-200 rounded-xl z-30 overflow-hidden" style={{ boxShadow: '0 8px 24px rgba(0,0,0,0.12)' }}>
                                  <button onClick={() => { navigate(`/billing/quotation/${q.quotationNo}/print`); setOpenMenuId(null); }}
                                    className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer transition-colors text-left">
                                    <i className="ri-printer-line text-gray-400" />Print / PDF
                                  </button>
                                  <button onClick={() => handleDuplicate(q)}
                                    className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer transition-colors text-left">
                                    <i className="ri-file-copy-line text-gray-400" />Duplicate
                                  </button>
                                  {q.status === 'draft' && (
                                    <button onClick={() => { setStatusTarget({ q, s: 'sent' }); setOpenMenuId(null); }}
                                      className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer transition-colors text-left">
                                      <i className="ri-send-plane-line text-gray-400" />Mark as Sent
                                    </button>
                                  )}
                                  {(q.status === 'sent' || q.status === 'draft') && (
                                    <button onClick={() => { setStatusTarget({ q, s: 'pending_approval' }); setOpenMenuId(null); }}
                                      className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer transition-colors text-left">
                                      <i className="ri-time-line text-gray-400" />Mark Pending Approval
                                    </button>
                                  )}
                                  {q.status === 'pending_approval' && (
                                    <>
                                      <button onClick={() => { setStatusTarget({ q, s: 'approved' }); setOpenMenuId(null); }}
                                        className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-amber-700 hover:bg-amber-50 cursor-pointer transition-colors text-left">
                                        <i className="ri-checkbox-circle-line text-amber-500" />Approve
                                      </button>
                                      <button onClick={() => { setStatusTarget({ q, s: 'rejected' }); setOpenMenuId(null); }}
                                        className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 cursor-pointer transition-colors text-left">
                                        <i className="ri-close-circle-line text-red-400" />Reject
                                      </button>
                                    </>
                                  )}
                                  {q.status === 'approved' && !q.convertedJobNo && (
                                    <>
                                      <button onClick={() => { setConvertTarget(q); setOpenMenuId(null); }}
                                        className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-teal-700 hover:bg-teal-50 cursor-pointer transition-colors text-left">
                                        <i className="ri-file-text-line text-teal-500" />Convert to Invoice
                                      </button>
                                      <button onClick={() => { setStatusTarget({ q, s: 'converted' }); setOpenMenuId(null); navigate('/job-cards/new'); }}
                                        className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-amber-700 hover:bg-amber-50 cursor-pointer transition-colors text-left">
                                        <i className="ri-arrow-right-circle-line text-amber-500" />Convert to Job Card
                                      </button>
                                    </>
                                  )}
                                  <div className="border-t border-gray-100" />
                                  <button onClick={() => { setDeleteTarget(q); setOpenMenuId(null); }}
                                    className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 cursor-pointer transition-colors text-left">
                                    <i className="ri-delete-bin-line text-red-400" />Delete
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Footer */}
          {!loading && (
            <div className="px-4 py-3 border-t border-gray-100 bg-gray-50/50 flex items-center justify-between">
              <p className="text-xs text-gray-500">
                Showing <strong>{sorted.length}</strong> of <strong>{quotations.length}</strong> quotations
              </p>
              <p className="text-xs text-gray-500">
                Filtered total: <strong className="text-gray-800">₹{sorted.reduce((a, b) => a + b.grandTotal, 0).toLocaleString('en-IN')}</strong>
              </p>
            </div>
          )}
        </div>
      </div>
    </ERPLayout>
  );
}
